import { Status } from "@/types/Status"
import dayjs from "dayjs"
import { AccountStatuses } from "@/models/account/account.types"

export const getStatus = (cancelled, endDate, startDate, balance): Status => {
  const today = dayjs().startOf("day")

  if (cancelled) {
    return new Status("default", AccountStatuses.CANCELLED)
  }

  if (!endDate && startDate < today) {
    return new Status("normal", AccountStatuses.ACTIVE)
  }
  if (!endDate && startDate >= today) {
    return new Status("neutral", AccountStatuses.NOT_ON_SUPPLY)
  }
  if (endDate && balance === 0) {
    return new Status("critical", AccountStatuses.CLOSED)
  }
  if (endDate && balance !== 0) {
    return new Status("neutral", AccountStatuses.TERMINATING)
  }

  throw Error("Account status could not be determined")
}

export const getActive = (status): boolean => {
  return status.text === AccountStatuses.ACTIVE
}

export const getIsTerminating = (status): boolean => {
  return status.text === AccountStatuses.TERMINATING
}

export const getAccountAge = (startDate, endDate): string => {
  if (endDate) {
    return startDate.from(endDate, true)
  } else {
    return startDate.from(dayjs(), true)
  }
}

export const getBalanceStatus = (balance): Status => {
  if (balance > 0) {
    return new Status("critical", "Debit")
  } else if (balance === 0) {
    return new Status("neutral", "")
  } else {
    return new Status("normal", "Credit")
  }
}

/**
 * Formats an account number by ensuring it has a specific length and padding it with leading '0's if necessary.
 *
 * @param {string} accountNumber - The account number to be formatted.
 * @returns {string} The formatted account number with a total of 8 digits, padded with leading zeros if the input is shorter.
 *
 * @example
 * formatAccountNumber("12345") // returns "00012345"
 * formatAccountNumber("12341234") // returns "12341234"
 */
export function formatAccountNumber(accountNumber: string): string {
  const numberOfDigits = 8
  return accountNumber.padStart(numberOfDigits, "0").trim()
}
